(() => {
  const mobileMql = window.matchMedia('(max-width: 749px)');
  const reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');

  const DEFAULT_INTERVAL_MS = 4500;
  const USER_PAUSE_MS = 10000;

  function onMediaQueryChange(mql, handler) {
    if (!mql) return;
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', handler);
    else if (typeof mql.addListener === 'function') mql.addListener(handler);
  }

  function isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.bottom > 0 && rect.top < viewportHeight;
  }

  function getClosestSlideIndex(slider, slides) {
    if (!slider || !slides.length) return 0;

    const scrollLeft = slider.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  function setupGalleryAutoplay(mediaGallery) {
    if (!mediaGallery || mediaGallery.dataset.autoplayInitialized === 'true') return;
    mediaGallery.dataset.autoplayInitialized = 'true';

    const slider = mediaGallery.querySelector('[id^="Slider-Gallery"]');
    if (!slider) return;

    const autoplaySpeedSeconds = Number.parseFloat(mediaGallery.dataset.autoplaySpeed || '');
    const intervalMs = Number.isFinite(autoplaySpeedSeconds) && autoplaySpeedSeconds > 0
      ? autoplaySpeedSeconds * 1000
      : DEFAULT_INTERVAL_MS;

    let timeoutId = null;
    let pausedUntil = 0;
    let isAutoScrolling = false;

    const shouldRun = () => {
      // If autoplay is explicitly enabled via data attribute, work on all devices
      if (mediaGallery.dataset.autoplaySpeed) {
        return !reducedMotionMql.matches && document.visibilityState === 'visible';
      }
      // Otherwise, maintain backward compatibility with mobile-only autoplay
      return mobileMql.matches && !reducedMotionMql.matches && document.visibilityState === 'visible';
    };

    const getSlides = () => Array.from(slider.querySelectorAll('[data-media-id]'));

    const schedule = (delayOverrideMs) => {
      window.clearTimeout(timeoutId);
      timeoutId = null;
      if (!shouldRun()) return;

      const now = Date.now();
      const delayFromPause = pausedUntil > now ? pausedUntil - now : 0;
      const delay = typeof delayOverrideMs === 'number' ? delayOverrideMs : intervalMs;
      timeoutId = window.setTimeout(tick, Math.max(delayFromPause, delay));
    };

    const pause = (ms = USER_PAUSE_MS) => {
      pausedUntil = Math.max(pausedUntil, Date.now() + ms);
      schedule(0);
    };

    const tick = () => {
      if (!shouldRun()) return;
      if (!isElementInViewport(mediaGallery)) return schedule(intervalMs);

      const slides = getSlides();
      if (slides.length < 2) return;

      const currentIndex = getClosestSlideIndex(slider, slides);
      const nextIndex = (currentIndex + 1) % slides.length;
      const nextId = slides[nextIndex]?.dataset?.mediaId;

      if (nextId && typeof mediaGallery.setActiveMedia === 'function') {
        isAutoScrolling = true;
        mediaGallery.setActiveMedia(nextId, false);
        window.setTimeout(() => {
          isAutoScrolling = false;
        }, 800);
      } else if (slides[nextIndex]) {
        isAutoScrolling = true;
        slider.scrollTo({ left: slides[nextIndex].offsetLeft, behavior: 'smooth' });
        window.setTimeout(() => {
          isAutoScrolling = false;
        }, 800);
      }

      schedule(intervalMs);
    };

    slider.addEventListener('pointerdown', () => pause(), { passive: true });
    slider.addEventListener('touchstart', () => pause(), { passive: true });
    slider.addEventListener('wheel', () => pause(), { passive: true });
    slider.addEventListener(
      'scroll',
      () => {
        if (isAutoScrolling) return;
        pause(3000);
      },
      { passive: true }
    );

    mediaGallery.querySelectorAll('[data-media-dots] [data-target]').forEach((dot) => {
      dot.addEventListener('click', () => pause(), { passive: true });
    });

    document.addEventListener('visibilitychange', () => schedule(0));
    onMediaQueryChange(mobileMql, () => schedule(0));
    onMediaQueryChange(reducedMotionMql, () => schedule(0));

    schedule(1000);
  }

  function init() {
    document.querySelectorAll('media-gallery[id^="MediaGallery-"]').forEach(setupGalleryAutoplay);
  }

  if (window.customElements?.whenDefined) {
    window.customElements.whenDefined('media-gallery').then(init);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
